import React, { useEffect, useState } from "react";
import { Link, useSearchParams, useLocation } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { LocationSearch } from "@/components/LocationSearch";
import { api } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { CountUp } from "@/components/CountUp";
import { Seo } from "@/components/Seo";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import {
  ArrowRight, BadgeCheck, Users, Clock, BarChart3, Flame, Award, MailCheck, Sparkles, Building2, Quote,
} from "lucide-react";
import { toast } from "sonner";

const PARTNERS_IMAGE = "https://images.unsplash.com/photo-1758599668299-beebedfabf7b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const OG_IMAGE = "https://civichearth.com/og-partners.png";

// DEMO CONTENT — founding-partner quote seeded with the GreenFields demo org.
// Swap the fields below for a real partner's words before/after launch; the
// section renders whatever is here, nothing else references it.
const FOUNDING_PARTNER_QUOTE = {
  quote: "We posted our first role on a Tuesday and had matched volunteers by the weekend. The one-click hour approvals alone save us an evening a week.",
  name: "GreenFields Trust",
  role: "Founding partner · Community gardens, London",
  impactDemoUrl: "/o/user_demo_org_green/impact",
};

const FAQS = [
  {
    q: "Is it really free?",
    a: "Yes — 100% free for causes, with no card details and no premium tier hiding the good stuff. CivicHearth is funded by voluntary donations from the community, not by charging organisations.",
  },
  {
    q: "Who can apply — do we have to be a registered charity?",
    a: "Registered charities, CICs, schools, sports clubs and informal community groups are all welcome. The verified badge is granted after a manual review, and being registered makes that quicker — but plenty of brilliant groups aren't registered, and residents can also host community-led events directly through Collectives.",
  },
  {
    q: "How long does verification take?",
    a: "Our team reviews every application by hand, typically within a few days. You'll get an email and an in-app notification either way — and you can sign in, finish your profile and draft your first opportunity while you wait.",
  },
  {
    q: "Do we need a charity or company registration number?",
    a: "No. It helps our review, but a website or active social media presence works too. If we can see you're a genuine cause, you're in.",
  },
  {
    q: "What if we don't want to sign in with Google?",
    a: "Sign-in is currently via Google using the email you applied with — you can attach a Google account to almost any existing email address (including your own domain). If that's a blocker for your organisation, mention it in the application note and we'll help you get set up.",
  },
  {
    q: "What do you do with our data?",
    a: "We use your details only to review your application and run the platform. We never sell data, and UK GDPR rights are built in — every account can export or permanently delete its data at any time.",
  },
];

const BENEFITS = [
  { icon: Users, t: "Matched volunteers", d: "Your roles are surfaced to people whose values and skills actually fit — not just whoever scrolls past." },
  { icon: Flame, t: "Swipe through candidates", d: "Quickly swipe through applicants and interested volunteers, shortlist the best, and accept in one tap." },
  { icon: Clock, t: "Effortless hour approvals", d: "Volunteers log hours with a narrative; you approve in one click. Everything counts toward their badges." },
  { icon: BarChart3, t: "Impact analytics", d: "Hours mobilised, retention, top contributors and weekly trends — exportable as CSV." },
  { icon: BadgeCheck, t: "Verified badge", d: "A trust mark on your public page and every opportunity you post. Founding partners get fast-tracked." },
  { icon: Award, t: "Recognition built in", d: "Auto-generated certificates and badges keep your volunteers motivated and coming back." },
];

const STEPS = [
  { n: "01", t: "Apply for fast-track", d: "Fill in the form — it takes a minute and puts you straight into our verification queue." },
  { n: "02", t: "Get verified", d: "Our team reviews your details manually. You'll be notified as soon as you're approved." },
  { n: "03", t: "Post your first role", d: "Sign in with Google using the email you applied with — that links your application to your account. Finish your profile and publish your first opportunity in minutes. (Prefer a different email? Just say so in your application note.)" },
];

function FastTrackForm() {
  const [sp] = useSearchParams();
  const refCode = sp.get("ref") || "";
  const [form, setForm] = useState({ org_name: "", email: "", website: "", registration_number: "", location: "", lat: null, lng: null, note: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.org_name.trim() || !form.email.trim()) return toast.error("Organisation name and email are required");
    setSubmitting(true);
    try {
      const res = await api.partnerApply({ ...form, ref: refCode });
      setDone({ email: form.email, status: res.status });
      toast.success("Application received!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit — please try again");
    }
    setSubmitting(false);
  };

  if (done) {
    return (
      <div data-testid="partner-apply-success" className="text-center py-10">
        <MailCheck className="w-12 h-12 text-forest mx-auto" />
        <div className="font-display text-2xl text-forest mt-4">
          {done.status === "verified" ? "You're already verified!" : "You're in the queue"}
        </div>
        <p className="text-sm text-neutral-600 mt-3 max-w-sm mx-auto leading-relaxed">
          {done.status === "verified"
            ? "This organisation is already verified on CivicHearth. Sign in to manage your opportunities."
            : <>We've fast-tracked <b>{done.email}</b> into our verification queue and sent you a confirmation. Sign in with Google using that email to finish your profile.</>}
        </p>
        <Link to="/login">
          <Button data-testid="partner-success-login" className="rounded-full bg-forest hover:bg-forest-hover mt-6 px-6 h-11">
            Sign in to CivicHearth <ArrowRight className="w-4 h-4 ml-1.5" />
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <form onSubmit={submit}>
      <div className="font-display text-2xl text-forest">Fast-track application</div>
      <div data-testid="partner-free-chip" className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-forest/5 border border-forest/15 px-3.5 py-1.5 text-xs font-semibold text-forest">
        <Sparkles className="w-3.5 h-3.5 text-terracotta" /> 100% free · no card details · no catch
      </div>
      <p className="text-sm text-neutral-500 mt-2">Takes a minute. Lands you straight in our verification queue.</p>
      <div className="space-y-3 mt-6">
        <Input data-testid="partner-org-name" placeholder="Organisation name *" value={form.org_name} onChange={set("org_name")} className="h-11 rounded-xl bg-white" />
        <Input data-testid="partner-email" type="email" placeholder="Contact email (you'll sign in with this) *" value={form.email} onChange={set("email")} className="h-11 rounded-xl bg-white" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Input data-testid="partner-website" placeholder="Website" value={form.website} onChange={set("website")} className="h-11 rounded-xl bg-white" />
          <Input data-testid="partner-reg" placeholder="Charity / reg. number" value={form.registration_number} onChange={set("registration_number")} className="h-11 rounded-xl bg-white" />
        </div>
        <LocationSearch
          testId="partner-location"
          value={form.location}
          placeholder="Town, city or UK postcode (e.g. London or SW1A 1AA)"
          onSelect={(r) => setForm(f => ({ ...f, location: r.label, lat: r.lat, lng: r.lng }))}
          onTextChange={(v) => setForm(f => ({ ...f, location: v, lat: null, lng: null }))}
        />
        <Textarea data-testid="partner-note" rows={3} placeholder="Tell us about your cause and the help you need" value={form.note} onChange={set("note")} className="rounded-xl bg-white" />
      </div>
      <Button data-testid="partner-submit" type="submit" disabled={submitting} className="rounded-full bg-terracotta hover:bg-terracotta-hover w-full h-12 mt-5 text-base shadow-lg shadow-terracotta/20">
        {submitting ? "Submitting…" : "Apply for fast-track"}
      </Button>
      <p className="text-[11px] text-neutral-400 mt-3 text-center">We review every application manually. No spam, ever.</p>
    </form>
  );
}

export default function Partners() {
  const [stats, setStats] = useState(null);
  const [areas, setAreas] = useState([]);
  const { hash } = useLocation();
  const { scrollY } = useScroll();
  const blobY = useTransform(scrollY, [0, 500], [0, 70]);

  useEffect(() => { api.publicStats().then(setStats).catch(() => {}); }, []);
  useEffect(() => { api.publicAreas().then(setAreas).catch(() => setAreas([])); }, []);
  // Footer "For councils" deep-link (#councils) — scroll once content exists.
  useEffect(() => {
    if (!hash) return;
    const t = setTimeout(() => document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: "smooth" }), 150);
    return () => clearTimeout(t);
  }, [hash]);

  const hasStats = stats && (stats.total_hours || stats.volunteers);

  return (
    <div data-testid="partners-page" className="bg-cream overflow-x-clip">
      <Seo title="Partner with CivicHearth" description="Matched volunteers, one-click hour approvals, impact analytics and a verified badge — free for causes. Apply for fast-track verification in under a minute." image={OG_IMAGE} />
      {/* Hero — split with form */}
      <section className="relative overflow-hidden">
        <motion.div style={{ y: blobY }} className="pointer-events-none absolute -top-24 -left-24 w-[440px] h-[440px] rounded-full bg-forest/10 blur-3xl" />
        <div className="pointer-events-none absolute top-40 -right-24 w-[380px] h-[380px] rounded-full bg-terracotta/10 blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-6 pt-20 pb-20 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-6 animate-fade-up lg:pt-6">
            <div className="inline-flex items-center gap-2 rounded-full border border-forest/15 bg-white/80 backdrop-blur px-4 py-1.5 text-xs uppercase tracking-[0.18em] text-forest/80">
              <Building2 className="w-3.5 h-3.5" /> For organisations
            </div>
            <h1 className="font-display text-5xl sm:text-6xl font-bold tracking-tighter leading-[1.05] text-forest mt-7">
              Partner for purpose.<br /><span className="shine-text">Scale your impact.</span>
            </h1>
            <p className="text-forest/70 leading-relaxed text-lg mt-7 max-w-xl">
              CivicHearth brings you volunteers matched on values and skills — plus the tools to run them:
              approvals, messaging, analytics and recognition. Free for causes.
            </p>
            {hasStats ? (
              <div className="mt-10 flex flex-wrap gap-10">
                {[{ k: stats.total_hours, l: "Hours mobilised" }, { k: stats.volunteers, l: "Volunteers" }, { k: stats.organisations, l: "Organisations" }].map((s) => (
                  <div key={s.l}>
                    <div className="font-display text-3xl font-semibold text-forest"><CountUp value={s.k} /></div>
                    <div className="text-xs uppercase tracking-[0.18em] text-forest/50 mt-1">{s.l}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="mt-10 inline-flex items-center gap-2 text-sm text-forest/70 bg-white/80 backdrop-blur border border-forest/10 rounded-full px-5 py-2.5">
                <Sparkles className="w-4 h-4 text-terracotta" /> Founding partners are showcased on our landing page from day one.
              </div>
            )}
            <div className="mt-8">
              <Link to="/opportunities" className="text-sm font-medium text-forest hover:text-forest-hover inline-flex items-center gap-1.5">
                See live opportunities <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>

          <motion.div
            id="apply"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-6"
          >
            <div className="civic-glass rounded-3xl p-8 sm:p-10 shadow-xl shadow-forest/10">
              <FastTrackForm />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <Reveal>
          <div className="text-xs uppercase tracking-[0.2em] text-terracotta/80 font-bold">Why CivicHearth</div>
          <h2 className="font-display text-4xl sm:text-5xl text-forest mt-4 leading-tight tracking-tight max-w-2xl">Everything you need to run volunteers, in one loop</h2>
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-10">
          {BENEFITS.map((b, i) => (
            <Reveal key={b.t} delay={(i % 3) * 0.1}>
              <div data-testid={`partner-benefit-${i}`} className="bg-white border border-forest/10 rounded-3xl p-9 h-full transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:shadow-forest/5">
                <div className="w-12 h-12 rounded-2xl bg-terracotta/10 flex items-center justify-center">
                  <b.icon className="w-6 h-6 text-terracotta" />
                </div>
                <div className="font-display text-xl text-forest mt-5">{b.t}</div>
                <div className="text-forest/70 mt-2.5 text-sm leading-relaxed">{b.d}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Image band + steps */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
          <Reveal className="lg:col-span-5">
            <img src={PARTNERS_IMAGE} alt="Corporate team volunteering outdoors" className="w-full h-[460px] object-cover rounded-3xl border border-forest/10" />
          </Reveal>
          <div className="lg:col-span-7">
            <Reveal>
              <div className="text-xs uppercase tracking-[0.2em] text-terracotta/80 font-bold">How it works</div>
              <h2 className="font-display text-4xl sm:text-5xl text-forest mt-4 leading-tight tracking-tight">Three steps to your first volunteer</h2>
            </Reveal>
            <div className="mt-10 space-y-7">
              {STEPS.map((s, i) => (
                <Reveal key={s.n} delay={i * 0.12}>
                  <div className="flex gap-6">
                    <div className="font-display text-3xl text-terracotta/60 shrink-0 w-12">{s.n}</div>
                    <div>
                      <div className="font-display text-2xl text-forest">{s.t}</div>
                      <div className="text-forest/70 mt-1.5 leading-relaxed">{s.d}</div>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Social proof — founding partner (DEMO content, see FOUNDING_PARTNER_QUOTE) */}
      <section className="max-w-7xl mx-auto px-6 py-14">
        <Reveal>
          <div data-testid="partner-quote" className="bg-cream border border-forest/10 rounded-3xl p-8 sm:p-12 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-8">
              <Quote className="w-8 h-8 text-terracotta/50" />
              <blockquote className="font-display text-2xl sm:text-3xl text-forest leading-snug mt-4">
                “{FOUNDING_PARTNER_QUOTE.quote}”
              </blockquote>
              <div className="mt-5 text-sm">
                <span className="font-semibold text-forest">{FOUNDING_PARTNER_QUOTE.name}</span>
                <span className="text-neutral-500"> — {FOUNDING_PARTNER_QUOTE.role}</span>
              </div>
            </div>
            <div className="lg:col-span-4 lg:text-right">
              <div className="text-xs uppercase tracking-[0.2em] text-terracotta/80 font-bold">Your shop window</div>
              <p className="text-sm text-forest/70 mt-2">Every verified organisation gets a public impact page — hours, volunteers and stories, ready to share with funders.</p>
              <Link
                data-testid="impact-page-preview-link"
                to={FOUNDING_PARTNER_QUOTE.impactDemoUrl}
                className="inline-flex items-center gap-1.5 mt-4 text-forest font-semibold underline underline-offset-4 hover:text-terracotta transition-colors"
              >
                See a live impact page <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </Reveal>
      </section>

      {/* For councils (17-D item 7) */}
      <section id="councils" className="max-w-7xl mx-auto px-6 py-14" data-testid="partners-councils">
        <Reveal>
          <div className="bg-forest rounded-3xl p-8 sm:p-12 text-white grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7">
              <div className="text-xs uppercase tracking-[0.2em] text-terracotta font-bold">For councils</div>
              <h2 className="font-display text-3xl sm:text-4xl mt-3 leading-tight tracking-tight">
                Your borough's social energy, measured
              </h2>
              <p className="text-white/75 mt-4 leading-relaxed">
                Councils, housing associations and funders get live, aggregate-only impact dashboards
                per area — volunteer hours, help requests fulfilled, collectives and gatherings — plus
                a printable pack for committee papers. Privacy by default: any figure drawn from fewer
                than 5 people is suppressed.
              </p>
              <a href="mailto:hello@civichearth.com?subject=Council%20partnership" className="inline-block mt-5 text-terracotta font-semibold underline underline-offset-4">
                hello@civichearth.com
              </a>
              {/* 2026-07: social prescribing promoted into the councils pitch */}
              <div className="mt-7 bg-white/10 border border-white/15 rounded-2xl p-6" data-testid="councils-social-prescribing">
                <div className="font-display text-xl">Social prescribing, built in</div>
                <p className="text-white/75 text-sm mt-2 leading-relaxed">
                  Link workers and GPs can refer someone in minutes — mediated,
                  consent-first, and PII-minimal by design.
                </p>
                <div className="flex items-center gap-4 mt-4 flex-wrap">
                  <Link
                    to="/for-professionals"
                    data-testid="councils-professionals-cta"
                    className="inline-flex items-center rounded-full bg-terracotta hover:bg-terracotta-hover text-white px-5 py-2.5 text-sm font-medium transition-colors"
                  >
                    For professionals
                  </Link>
                  <Link
                    to="/safeguarding"
                    data-testid="councils-safeguarding-link"
                    className="text-sm text-white/70 underline underline-offset-2 hover:text-white"
                  >
                    Our safeguarding policy
                  </Link>
                </div>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="text-xs uppercase tracking-[0.2em] text-white/50 font-bold">Live area dashboards</div>
              {areas.length === 0 ? (
                <p className="text-sm text-white/60 mt-3">First area dashboards are being switched on now — ask us for a preview.</p>
              ) : (
                <div className="mt-3 space-y-2">
                  {areas.map((a) => (
                    <Link
                      key={a.slug}
                      to={`/impact/${a.slug}`}
                      data-testid={`council-impact-link-${a.slug}`}
                      className="flex items-center justify-between bg-white/10 hover:bg-white/15 rounded-xl px-5 py-3.5 transition-colors"
                    >
                      <span className="font-medium">{a.name}</span>
                      <ArrowRight className="w-4 h-4 text-terracotta" />
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>
        </Reveal>
      </section>

      {/* Schools & DofE groups (17-D item 8 — copy only) */}
      <section id="schools" className="max-w-7xl mx-auto px-6 py-4" data-testid="partners-schools">
        <Reveal>
          <div className="bg-white border border-forest/10 rounded-3xl p-8 sm:p-12 grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-7">
              <div className="text-xs uppercase tracking-[0.2em] text-terracotta/80 font-bold">Schools &amp; DofE groups</div>
              <h2 className="font-display text-3xl sm:text-4xl text-forest mt-3 leading-tight tracking-tight">
                Evidence that writes itself
              </h2>
              <p className="text-forest/70 mt-4 leading-relaxed">
                Students log hours as they volunteer; organisations approve them with one click. When
                it's time for eDofE or a UCAS statement, they generate a date-ranged evidence pack with
                a public verification code — and can request a prefilled assessor report their
                organisation approves in seconds. No chasing signatures, no lost paper cards.
              </p>
            </div>
            <div className="lg:col-span-5 self-center">
              <ul className="space-y-3 text-sm text-forest/80">
                <li className="flex gap-2.5"><span className="text-terracotta font-bold">1.</span> Approved-hours table with narratives, per date range</li>
                <li className="flex gap-2.5"><span className="text-terracotta font-bold">2.</span> Tamper-proof verification at civichearth.com/verify</li>
                <li className="flex gap-2.5"><span className="text-terracotta font-bold">3.</span> Copy-ready assessor reports for eDofE</li>
              </ul>
              <a href="mailto:hello@civichearth.com?subject=Schools%20%26%20DofE" className="inline-block mt-5 text-forest font-semibold underline underline-offset-4 hover:text-terracotta transition-colors">
                Talk to us about your school or group
              </a>
            </div>
          </div>
        </Reveal>
      </section>

      {/* 18-B2 item 7: employer volunteering-day enquiry (concierge, no billing) */}
      <section id="employers" className="max-w-7xl mx-auto px-6 py-14" data-testid="partners-employers">
        <Reveal>
          <div className="bg-cream border border-forest/10 rounded-3xl p-8 sm:p-12 grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-6">
              <div className="text-xs uppercase tracking-[0.2em] text-terracotta/80 font-bold">Employers</div>
              <h2 className="font-display text-3xl sm:text-4xl text-forest mt-3 leading-tight tracking-tight">
                Give your team a volunteering day that actually matters locally
              </h2>
              <p className="text-forest/70 mt-4 leading-relaxed">
                Employer teams volunteer together on real neighbourhood needs, track their
                person-hours, and get a print-ready quarterly summary for the office wall.
                No subscriptions, no per-seat pricing — tell us roughly what you're after
                and we'll set it up with you.
              </p>
            </div>
            <div className="lg:col-span-6 self-center">
              <EmployerEnquiryForm />
            </div>
          </div>
        </Reveal>
      </section>

      {/* FAQ */}
      <section className="max-w-4xl mx-auto px-6 py-14" data-testid="partners-faq">
        <Reveal>
          <div className="text-xs uppercase tracking-[0.2em] text-terracotta/80 font-bold">Questions, answered</div>
          <h2 className="font-display text-4xl text-forest mt-4 tracking-tight">Before you apply</h2>
        </Reveal>
        <Accordion type="single" collapsible className="mt-8">
          {FAQS.map((f, i) => (
            <AccordionItem key={f.q} value={`faq-${i}`}>
              <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-left font-medium text-forest hover:text-terracotta hover:no-underline">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-neutral-600 leading-relaxed">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* Bottom CTA */}
      <section className="bg-forest text-white py-24 mt-10 relative overflow-hidden">
        <div className="pointer-events-none absolute -bottom-24 left-1/4 w-[420px] h-[420px] rounded-full bg-terracotta/15 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-6 text-center">
          <Reveal>
            <h2 className="font-display text-4xl sm:text-5xl leading-tight tracking-tight">Ready to unlock your community?</h2>
            <p className="text-white/80 mt-4 max-w-lg mx-auto text-lg">Join the founding cohort of causes on CivicHearth.</p>
            <a href="#apply">
              <Button className="rounded-full bg-terracotta hover:bg-terracotta-hover mt-8 px-8 h-[52px] text-base shadow-lg shadow-black/20 transition-transform hover:-translate-y-0.5">Apply now</Button>
            </a>
          </Reveal>
        </div>
      </section>
    </div>
  );
}

// 18-B2 item 7: volunteering-day enquiry — {company, size, area, preferred_month}
function EmployerEnquiryForm() {
  const [form, setForm] = useState({ company: "", size: "", area: "", preferred_month: "" });
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async () => {
    if (!form.company.trim() || !form.size.trim() || !form.area.trim() || !form.preferred_month.trim()) {
      return toast.error("All four fields are needed so we can plan the day");
    }
    setBusy(true);
    try {
      await api.employerEnquiry(form);
      setSent(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send — try again shortly");
    }
    setBusy(false);
  };

  if (sent) {
    return (
      <div className="bg-white border border-forest/15 rounded-2xl p-8 text-center" data-testid="employer-enquiry-sent">
        <div className="font-display text-2xl text-forest">Thanks — we'll be in touch</div>
        <p className="text-sm text-forest/70 mt-2">Your enquiry is with our team. Expect a reply within a couple of working days.</p>
      </div>
    );
  }

  return (
    <div className="bg-white border border-forest/15 rounded-2xl p-6 space-y-3" data-testid="employer-enquiry-form">
      <div className="font-display text-xl text-forest">Volunteering day enquiry</div>
      <Input data-testid="enquiry-company" placeholder="Company name" value={form.company} onChange={set("company")} className="h-11" />
      <div className="grid grid-cols-2 gap-3">
        <select data-testid="enquiry-size" value={form.size} onChange={set("size")} className="h-11 rounded-md border border-neutral-200 bg-white px-3 text-sm">
          <option value="">Team size</option>
          <option value="1-10">1–10 people</option>
          <option value="11-50">11–50 people</option>
          <option value="51-200">51–200 people</option>
          <option value="200+">200+ people</option>
        </select>
        <Input data-testid="enquiry-month" type="month" value={form.preferred_month} onChange={set("preferred_month")} className="h-11" title="Preferred month" />
      </div>
      <Input data-testid="enquiry-area" placeholder="Area (e.g. Peckham, Bristol BS1)" value={form.area} onChange={set("area")} className="h-11" />
      <Button data-testid="enquiry-submit" onClick={submit} disabled={busy} className="w-full rounded-full bg-forest hover:bg-forest-hover h-11">
        {busy ? "Sending…" : "Send enquiry"}
      </Button>
    </div>
  );
}
